import { DeleteOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { App, Button, Card, Modal, Table } from 'antd';
import Layout from 'components/Layout';
import withGuard from 'components/withGuard';
import { PERMISSIONS } from 'constants/permissions';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { captureException } from 'utils/errors';
import { getData } from 'utils';
import { deleteSegment, getSegment, getSegmentCustomers } from 'api/segments';
import { priceTransformer } from 'utils';
import CreateSegmentModal from '../CreateSegmentModal';
import { formatConditionForDisplay } from '../constants';

const EXPORT_PAGE_SIZE = 500;

function escapeCsvValue(val) {
  if (val == null || val === '') return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function downloadCsv(rows, columns, filename) {
  const header = columns.map((c) => escapeCsvValue(c.title)).join(',');
  const lines = [header, ...rows.map((row) => columns.map((c) => escapeCsvValue(row[c.dataIndex] ?? row[c.key])).join(','))];
  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function SegmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [segment, setSegment] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0 });

  const pageSize = 10;

  const loadSegment = useCallback(() => {
    if (!id) return;
    setLoading(true);
    getSegment(id)
      .then((res) => {
        const data = getData(res)?.data ?? getData(res);
        setSegment(data);
        setLoading(false);
      })
      .catch((e) => {
        captureException(e);
        setLoading(false);
      });
  }, [id]);

  const loadCustomers = useCallback(() => {
    if (!id) return;
    setCustomersLoading(true);
    getSegmentCustomers(id, { page, page_size: pageSize })
      .then((res) => {
        const data = getData(res)?.data ?? getData(res);
        const results = data?.results ?? [];
        setCustomers(Array.isArray(results) ? results : []);
        setPagination({ count: data?.count ?? 0 });
        setCustomersLoading(false);
      })
      .catch((e) => {
        captureException(e);
        setCustomersLoading(false);
      });
  }, [id, page]);

  const handleExportCsv = useCallback(async () => {
    if (!id || !segment) return;
    setExporting(true);
    const allCustomers = [];
    let pageNum = 1;
    let hasMore = true;
    try {
      while (hasMore) {
        const res = await getSegmentCustomers(id, { page: pageNum, page_size: EXPORT_PAGE_SIZE });
        const data = getData(res)?.data ?? getData(res);
        const results = data?.results ?? [];
        allCustomers.push(...(Array.isArray(results) ? results : []));
        const total = data?.count ?? 0;
        hasMore = allCustomers.length < total;
        pageNum += 1;
      }
      const exportColumns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Email', dataIndex: 'email', key: 'email' },
        { title: 'Phone', dataIndex: 'phone', key: 'phone' },
        { title: 'Total Spend', dataIndex: 'total_spent', key: 'total_spent' },
        { title: 'Orders', dataIndex: 'orders_count', key: 'orders_count' },
      ];
      const filename = `${(segment.name || 'segment').replace(/[^a-z0-9-_]/gi, '_')}_customers.csv`;
      downloadCsv(allCustomers, exportColumns, filename);
      message.success(`Exported ${allCustomers.length} customers`);
    } catch (e) {
      captureException(e);
    } finally {
      setExporting(false);
    }
  }, [id, segment, message]);

  const handleEditSuccess = useCallback(() => {
    setEditModalOpen(false);
    loadSegment();
    loadCustomers();
  }, [loadSegment, loadCustomers]);

  const handleDeleteSegment = useCallback(() => {
    if (!id || !segment) return;
    Modal.confirm({
      title: 'Delete segment',
      content: `Are you sure you want to delete "${segment.name}"? This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () =>
        deleteSegment(id)
          .then(() => {
            message.success('Segment deleted');
            navigate('/console/segments');
          })
          .catch(captureException),
    });
  }, [id, segment, message, navigate]);

  useEffect(() => {
    loadSegment();
  }, [loadSegment]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const formatConditions = (conditions) => {
    if (!conditions || !Array.isArray(conditions)) return '-';
    return (
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {conditions.map((c, i) => (
          <li key={i} style={{ marginBottom: 4 }}>
            {formatConditionForDisplay(c)}
          </li>
        ))}
      </ul>
    );
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/console/customers/${row.id}`);
          }}
        >
          {name || 'No name'}
        </a>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Total Spend',
      dataIndex: 'total_spent',
      key: 'total_spent',
      render: (val) => priceTransformer(val ?? 0),
    },
    {
      title: 'Orders',
      dataIndex: 'orders_count',
      key: 'orders_count',
    },
  ];

  if (loading || !segment) {
    return (
      <Layout>
        <div style={{ padding: 24 }}>Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout
      header={(
        <Layout.Header
          title={segment.name}
          backTitle="Segments"
          backPath="/console/segments"
          extra={[
            <Button
              key="edit"
              icon={<EditOutlined />}
              onClick={() => setEditModalOpen(true)}
            >
              Edit
            </Button>,
            <Button
              key="export"
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>,
            <Button
              key="delete"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDeleteSegment}
            >
              Delete
            </Button>,
          ]}
        />
      )}
    >
      <div style={{ padding: 24 }}>
        <Card style={{ marginBottom: 24 }}>
          {segment.description && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#717679', marginBottom: 4 }}>Description</div>
              <div>{segment.description}</div>
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#717679', marginBottom: 4 }}>Conditions</div>
            <div>{formatConditions(segment.conditions)}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#717679', marginBottom: 4 }}>Customers</div>
            <div style={{ fontWeight: 700, fontSize: 24 }}>{segment.customer_count ?? 0}</div>
          </div>
        </Card>

        <Card title="Customers in this segment">
          <Table
            loading={customersLoading}
            columns={columns}
            dataSource={customers}
            rowKey="id"
            pagination={{
              current: page,
              pageSize,
              total: pagination.count,
              showSizeChanger: false,
              onChange: setPage,
            }}
          />
        </Card>

        <CreateSegmentModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSuccess={handleEditSuccess}
          segment={segment}
          mode="edit"
        />
      </div>
    </Layout>
  );
}

export default withGuard(SegmentDetail, [PERMISSIONS.MARKETING], { redirect: true });
